import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import palette from "../../common/styles/palette";
import {
  AnswerForm,
  AnswerOption,
  AnswerTreeOption,
  InterviewAnswer,
} from "../../types/Interview";

/**
 * A forma de resposta, desenhada logo abaixo da pergunta do assistente.
 *
 * POR QUE ELA EXISTE. O professor digitou "Cardiologia e atendimento" e o
 * assistente respondeu que o tema não existe e "precisa ser cadastrado antes".
 * Isso o mandava para outra tela no meio da conversa — o oposto de manter a
 * bola no pé dele. Ele não sabe quais Temas existem e não vai adivinhar.
 *
 * DUAS REGRAS QUE NÃO SE QUEBRAM AQUI:
 *
 *  1. O componente é ATALHO, NÃO GAIOLA. O campo de texto do chat continua
 *     ativo ao lado; quem preferir digitar, digita, e cai no caminho de sempre.
 *  2. Responder por aqui manda o MESMO `turn`. Não há segundo canal, não há
 *     endpoint especial. Muda só o fato de a resposta já vir estruturada, o
 *     que permite ao servidor aplicá-la sem consultar o modelo.
 */
interface Props {
  forma: AnswerForm;
  busy: boolean;
  /** `rotulo` vai para a conversa como fala do professor. */
  onResponder: (rotulo: string, answer: InterviewAnswer) => void;
}

/** Lista longa vira busca; lista curta vira botão. */
const LIMIAR_BOTOES = 8;

export default function RespostaGuiada({ forma, busy, onResponder }: Props) {
  const { t } = useTranslation();
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");
  const [area, setArea] = useState("");
  const [subarea, setSubarea] = useState("");
  const [especialidade, setEspecialidade] = useState("");
  const [escolhido, setEscolhido] = useState<AnswerOption | null>(null);

  const opcoes = useMemo(
    () => (forma.opcoes || []).filter((o) => o.acao !== "criar"),
    [forma.opcoes]
  );
  const opcaoCriar = (forma.opcoes || []).find((o) => o.acao === "criar");

  const subareas = useMemo(
    () => (forma.arvore?.subareas || []).filter((s: AnswerTreeOption) => s.pai === area),
    [forma.arvore, area]
  );
  const especialidades = useMemo(
    () =>
      (forma.arvore?.especialidades || []).filter(
        (e: AnswerTreeOption) => e.pai === subarea
      ),
    [forma.arvore, subarea]
  );

  if (forma.tipo === "texto" || !forma.campo || (forma.opcoes || []).length === 0) {
    return null;
  }

  const campo = forma.campo;

  const escolher = (o: AnswerOption) =>
    onResponder(o.rotulo, { campo, valor: o.valor });

  const abrirCriacao = () => {
    setCriando(true);
    setNome(opcaoCriar?.nome || "");
  };

  const precisaClassificar = forma.tipo === "criar_tema";
  const podeCriar =
    nome.trim().length > 0 &&
    (!precisaClassificar || (area !== "" && subarea !== "" && especialidade !== ""));

  const confirmarCriacao = () => {
    if (!podeCriar) return;
    onResponder(`${t("interview_form_create")} **${nome.trim()}**`, {
      campo,
      criar: {
        nome: nome.trim(),
        ...(precisaClassificar
          ? {
              area_id: Number(area),
              subarea_id: Number(subarea),
              specialty_id: Number(especialidade),
            }
          : {}),
      },
    });
    setCriando(false);
  };

  return (
    <Box
      sx={{
        px: 2,
        pb: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        alignItems: "flex-start",
      }}
    >
      {!criando && (
        <>
          {/* Lista curta: botões. Lista longa: busca — 33 Temas em botões é
              parede, e o professor procura pelo nome que tem na cabeça. */}
          {opcoes.length <= LIMIAR_BOTOES && !forma.buscavel ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {opcoes.map((o) => (
                <Chip
                  key={o.valor}
                  label={o.rotulo}
                  disabled={busy}
                  onClick={() => escolher(o)}
                  sx={{
                    cursor: busy ? "default" : "pointer",
                    borderColor: palette.brand,
                    color: palette.brandDark,
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}>
              <Autocomplete
                size="small"
                sx={{ flex: 1, maxWidth: 520 }}
                options={opcoes}
                getOptionLabel={(o) => o.rotulo}
                isOptionEqualToValue={(a, b) => a.valor === b.valor}
                value={escolhido}
                onChange={(_, v) => setEscolhido(v)}
                disabled={busy}
                renderInput={(params) => (
                  <TextField {...params} label={t("interview_form_pick")} />
                )}
              />
              <Button
                variant="contained"
                size="small"
                disabled={busy || !escolhido}
                onClick={() => escolhido && escolher(escolhido)}
              >
                {t("interview_form_confirm")}
              </Button>
            </Box>
          )}

          {opcaoCriar && (
            <Button size="small" disabled={busy} onClick={abrirCriacao}>
              {opcaoCriar.rotulo}
            </Button>
          )}
        </>
      )}

      {criando && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            p: 1.5,
            width: "100%",
            border: `1px solid ${palette.border}`,
            borderRadius: 1,
            background: palette.surface,
          }}
        >
          <TextField
            size="small"
            label={t("interview_form_name")}
            value={nome}
            disabled={busy}
            onChange={(e) => setNome(e.target.value)}
            sx={{ minWidth: 260 }}
          />

          {/* Área → subárea → especialidade em cascata: as três chaves que
              `themes` exige. Não são herdadas de um padrão porque adivinhar
              chave estrangeira grava o Cenário na gaveta errada em silêncio. */}
          {precisaClassificar && (
            <>
              <TextField
                select
                size="small"
                label={t("interview_form_area")}
                value={area}
                disabled={busy}
                onChange={(e) => {
                  setArea(e.target.value);
                  setSubarea("");
                  setEspecialidade("");
                }}
                sx={{ minWidth: 180 }}
              >
                {(forma.arvore?.areas || []).map((a) => (
                  <MenuItem key={a.valor} value={a.valor}>
                    {a.rotulo}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t("interview_form_subarea")}
                value={subarea}
                disabled={busy || area === ""}
                onChange={(e) => {
                  setSubarea(e.target.value);
                  setEspecialidade("");
                }}
                sx={{ minWidth: 200 }}
              >
                {subareas.map((s) => (
                  <MenuItem key={s.valor} value={s.valor}>
                    {s.rotulo}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t("interview_form_specialty")}
                value={especialidade}
                disabled={busy || subarea === ""}
                onChange={(e) => setEspecialidade(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                {especialidades.map((e) => (
                  <MenuItem key={e.valor} value={e.valor}>
                    {e.rotulo}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}

          <Button variant="contained" size="small" disabled={busy || !podeCriar} onClick={confirmarCriacao}>
            {t("interview_form_create")}
          </Button>
          <Button size="small" disabled={busy} onClick={() => setCriando(false)}>
            {t("interview_form_cancel")}
          </Button>
        </Box>
      )}

      <Typography variant="caption" sx={{ color: palette.textMuted }}>
        {t("interview_form_or_type")}
      </Typography>
    </Box>
  );
}
